import {type Branded, make} from './brandedType'
import {audioFileExtensionToContentType, nonAudioFileExtensionToContentType} from './ExtensionConstants'
import {LanguageCode} from './locales'

export type SearchType = 'normal' | 'admin' | 'following' | 'discover'
export type HexColor = `#${string}`
export interface Range {
  min: number
  max: number
}

export enum GalleryContentType {
  ANY = 'ANY',
  TEMPLATES = 'TEMPLATES'
}

export const SYSTEM_USER_EMAIL = 'notability-system@gingerlabs.com'

export enum SearchFilter {
  pencil = 'pencil',
  study_tape = 'study_tape',
  audio = 'audio',
  remix = 'remix'
}

export enum CommentReviewState {
  needs_review = 'needs_review',
  approved = 'approved',
  hidden = 'hidden'
}

export type UserLanguage = LanguageCode | 'ANY'

/**
 * NOTE: If you add a parameter here, you'll also need to add it
 * to `GalleryNoteQueryResolver.ts` so that it will be supported
 * by the GraphQL endpoint as well.
 */
export interface CoreSearchParams {
  query?: string
  tags?: string[]
  screenname?: string
  pageSize?: number
  offset?: number
  forUserInChina?: boolean
  sortingParam?: NoteSortingKey
  trendingTimeframe?: number
  // Ignored if search type is 'following'
  language?: UserLanguage
  searchType?: SearchType
  contentType?: GalleryNoteContentType
  filters?: SearchFilter[]
  rootNoteId?: string

  // followed publishers that we want to include in our search (for the filter on the frontend.)
  // Ignored if the search type is not 'following'
  followedToInclude?: string[]
}

export enum GalleryNoteContentType {
  NOTE = 'NOTE',
  TEMPLATE = 'TEMPLATE'
}

/** Row kinds in the gallery's Discover feed (see `getDiscoverFeedRows` in common-backend). */
export enum DiscoverRowKind {
  COLLECTION = 'COLLECTION',
  TEMPLATES = 'TEMPLATES',
  TAG = 'TAG'
}

export enum ExperimentEnrollmentStrategy {
  EXPERIMENT = 'EXPERIMENT',
  ROLLOUT = 'ROLLOUT'
}

export type ClientCookieSetting = 'opt-out' | 'dismissed' | null

export enum NoteSortingKey {
  CREATION_DATE = 'CREATION_DATE',
  CREATION_DATE_ASC = 'CREATION_DATE_ASC',
  POPULAR_SCORE = 'POPULAR_SCORE',
  TRENDING = 'TRENDING'
}

export enum NoteUserPermissionRequestState {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

export enum TriageState {
  APPROVED = 'APPROVED',
  QUARANTINED = 'QUARANTINED',
  AWAITING_TRIAGE = 'AWAITING_TRIAGE'
}

export interface GithubRequestBase {
  owner: string
  repo: string
  per_page: number
}

export enum NotificationType {
  NOTE_LIKED = 'NOTE_LIKED',
  NOTE_COMMENTED_ON = 'NOTE_COMMENTED_ON',
  MENTIONED_IN_COMMENT = 'MENTIONED_IN_COMMENT',
  FOLLOWED = 'FOLLOWED',
  NOTE_FLAGGED = 'NOTE_FLAGGED',
  NOTE_APPROVED = 'NOTE_APPROVED',
  NOTE_QUARANTINED = 'NOTE_QUARANTINED',
  PROFILE_FLAGGED = 'PROFILE_FLAGGED',
  PROFILE_HIDDEN = 'PROFILE_HIDDEN',
  PROFILE_APPROVED = 'PROFILE_APPROVED',
  DESCRIPTION_MENTIONED = 'DESCRIPTION_MENTIONED',
  WELCOME = 'WELCOME',
  NOTE_REMIX_POSTED = 'NOTE_REMIX_POSTED',
  PROMOTION = 'PROMOTION'
}

export enum FlaggedReason {
  sexualContent = 'sexualContent',
  abusiveContent = 'abusiveContent',
  spam = 'spam',
  needsNewReview = 'needsNewReview',
  autoFlagPhoneNumber = 'autoFlagPhoneNumber',
  autoFlagEmailAddr = 'autoFlagEmailAddr',
  autoFlagSSN = 'autoFlagSSN',
  autoFlagMeetId = 'autoFlagMeetId',
  autoFlagURL = 'autoFlagURL',
  autoFlagBadword = 'autoFlagBadword',
  autoFlagRedFlagPhrase = 'autoFlagRedFlagPhrase',
  autoFlagCopyright = 'autoFlagCopyrightPhrase',
  underageUser = 'underageUser',
  other = 'other'
}

export enum GalleryNotesAdminEventType {
  'NOTE_FLAGGED' = 'NOTE_FLAGGED',
  'NOTE_APPROVED' = 'NOTE_APPROVED'
}

export enum UsersAdminEventType {
  USER_BLOCKED = 'USER_BLOCKED',
  USER_UNBLOCKED = 'USER_UNBLOCKED',
  PROFILE_FLAGGED = 'PROFILE_FLAGGED',
  PROFILE_HIDDEN = 'PROFILE_HIDDEN',
  PROFILE_APPROVED = 'PROFILE_APPROVED',
  EMAIL_CHANGED = 'EMAIL_CHANGED',
  SCREENNAME_CHANGED = 'SCREENNAME_CHANGED',
  SUBSCRIPTION_OVERRIDE_GRANTED = 'SUBSCRIPTION_OVERRIDE_GRANTED',
  SUBSCRIPTION_OVERRIDE_DELETED = 'SUBSCRIPTION_OVERRIDE_DELETED',
  USER_ROLE_CHANGED = 'USER_ROLE_CHANGED',
  COMMENT_REJECTED = 'COMMENT_REJECTED',
  OAUTH_ACCESS_REMOVED = 'OAUTH_ACCESS_REMOVED'
}

export enum UserStatus {
  ACTIVE = 'Active',
  PENDING_DELETION = 'Pending Deletion',
  // todo: (dc) rename this to INVITED_TO_BUSINESS in the code (ideally also the DB, but let's stay focused)
  ORG_INVITED = 'Org Invited'
}

export enum BusinessUserStatus {
  ACCEPTED = 'ACCEPTED',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
  INVITED = 'INVITED'
}

export enum BusinessJoinCandidateStatus {
  ELIGIBLE = 'ELIGIBLE',
  DECLINED = 'DECLINED',
  ACCEPTED = 'ACCEPTED',
  DENIED = 'DENIED'
}

export enum BusinessDomainStatus {
  REQUESTED = 'REQUESTED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

export enum OrganizationUserStatus {
  INVITED = 'Invited',
  ACCEPTED = 'Accepted',
  EXPIRED = 'Expired',
  FAILED = 'Failed'
}

export enum UserCreationMethod {
  LEGACY = 'legacy',
  PASSWORD = 'password',
  MAGIC_LINK = 'magicLink',
  OAUTH = 'oauth'
}

export enum UserUseType {
  PERSONAL = 'PERSONAL',
  STUDENT = 'STUDENT',
  EDUCATOR = 'EDUCATOR',
  BUSINESS = 'BUSINESS'
}

export enum PersonaType {
  PERSONAL = 'PERSONAL',
  STUDENT = 'STUDENT',
  EDUCATOR = 'EDUCATOR',
  BUSINESS = 'BUSINESS'
}

export enum CollabNoteRole {
  OWNER = 'OWNER',
  EDITOR = 'EDITOR',
  VIEWER = 'VIEWER'
}

export enum AccessLevel {
  EDITOR = 'EDITOR',
  VIEWER = 'VIEWER'
}

export enum PermissionScope {
  RESTRICTED = 'RESTRICTED',
  PUBLIC = 'PUBLIC'
}

export enum PermissionScopeV2 {
  RESTRICTED = 'RESTRICTED',
  PUBLIC = 'PUBLIC',
  BUSINESS_WIDE = 'BUSINESS_WIDE'
}

export enum MediaUploadStatusEnum {
  PENDING = 'PENDING',
  UPLOADED = 'UPLOADED',
  DELETED = 'DELETED'
}

export enum CustomAssetType {
  TEMPLATE = 'TEMPLATE',
  STICKER = 'STICKER'
}

export enum CollabNoteIndexType {
  HANDWRITING = 'HANDWRITING',
  PDF = 'PDF',
  IMAGE = 'IMAGE',
  TEXT = 'TEXT',
  RECORDING = 'RECORDING'
}

export enum MediaUploadExtension {
  PDF = 'pdf',
  PNG = 'png',
  JPEG = 'jpg',
  M4A = 'm4a',
  GIF = 'gif',
  WEBM = 'webm',
  WEBA = 'weba',
  MP3 = 'mp3',
  MP4 = 'mp4',
  WEBP = 'webp',
  AVIF = 'avif'
}

export enum ASRJobStatus {
  UPLOADING = 'UPLOADING',
  UPLOADED = 'UPLOADED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  TRANSIENT_ERROR = 'TRANSIENT_ERROR',
  PERMANENT_ERROR = 'PERMANENT_ERROR'
}

export enum LlmCostEventType {
  QUIZ_JOB = 'QUIZ_JOB',
  QUIZ_JOB_ERROR = 'QUIZ_JOB_ERROR',
  QUIZ_EXPLANATION = 'QUIZ_EXPLANATION',
  SUMMARY_JOB = 'SUMMARY_JOB',
  SUMMARY_JOB_ERROR = 'SUMMARY_JOB_ERROR',
  STUDY_CHAT_EXCHANGE = 'STUDY_CHAT_EXCHANGE',
  DISCUSSION_EXCHANGE = 'DISCUSSION_EXCHANGE',
  LIVE_SMART_NOTE = 'LIVE_SMART_NOTE',
  SYLLABUS_PARSE = 'SYLLABUS_PARSE',
  LLM_EVAL_JUDGE = 'LLM_EVAL_JUDGE',
  ASR_JOB = 'ASR_JOB',
  CHATBOT_TURN = 'CHATBOT_TURN',
  DIARIZATION_JOB = 'DIARIZATION_JOB',
  LIVE_TRANSCRIPTION = 'LIVE_TRANSCRIPTION',
  AUDIO_OVERVIEW = 'AUDIO_OVERVIEW',
  DUMMY_QUOTA = 'DUMMY_QUOTA'
}

export enum LlmCostSubjectType {
  QUIZ_JOBS = 'QUIZ_JOBS',
  SUMMARY_JOBS = 'SUMMARY_JOBS',
  STUDY_CHAT_EXCHANGE = 'STUDY_CHAT_EXCHANGE',
  DISCUSSION_EXCHANGE = 'DISCUSSION_EXCHANGE',
  LIVE_TRANSCRIPTION_SESSIONS = 'LIVE_TRANSCRIPTION_SESSIONS',
  LLM_EVAL_RUNS = 'LLM_EVAL_RUNS',
  ASR_TRANSCRIPTION_COMPLETE = 'ASR_TRANSCRIPTION_COMPLETE',
  CHATBOT_THREADS = 'CHATBOT_THREADS',
  DIARIZATION_COMPLETE = 'DIARIZATION_COMPLETE',
  STUDY_MATERIALS_BATCHES = 'STUDY_MATERIALS_BATCHES',
  DUMMY_QUOTA = 'DUMMY_QUOTA'
}

export enum QuizSessionQuestionType {
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  FLASHCARD = 'FLASHCARD',
  FILL_IN_THE_BLANK = 'FILL_IN_THE_BLANK',
  MIX_AND_MATCH = 'MIX_AND_MATCH',
  TRUE_OR_FALSE = 'TRUE_OR_FALSE',
  QUESTION_PROMISE = 'QUESTION_PROMISE'
}

// The question types a QuestionPromise can become; QUESTION_PROMISE names the placeholder itself, not a
// question. MIX_AND_MATCH takes one promise per flashcard it pairs; the rest take one each.
export type QuizPromiseQuestionType =
  | QuizSessionQuestionType.MULTIPLE_CHOICE
  | QuizSessionQuestionType.TRUE_OR_FALSE
  | QuizSessionQuestionType.FLASHCARD
  | QuizSessionQuestionType.FILL_IN_THE_BLANK
  | QuizSessionQuestionType.MIX_AND_MATCH

export enum QuizSessionMode {
  PRACTICE_MULTIPLE_CHOICE = 'PRACTICE_MULTIPLE_CHOICE',
  PRACTICE_FLASHCARDS = 'PRACTICE_FLASHCARDS',
  PRACTICE_FILL_IN_THE_BLANK = 'PRACTICE_FILL_IN_THE_BLANK',
  PRACTICE_MIX_AND_MATCH = 'PRACTICE_MIX_AND_MATCH',
  PRACTICE_TRUE_OR_FALSE = 'PRACTICE_TRUE_OR_FALSE',
  EXAM = 'EXAM',
  EXAM_MULTIPLE_CHOICE = 'EXAM_MULTIPLE_CHOICE',
  SPACED_REPETITION_FLASHCARDS = 'SPACED_REPETITION_FLASHCARDS'
}

// Append-only: values are stored in achievement_badges and mirrored into client enums.
export enum AchievementBadgeType {
  LEARN_ACTIVITY = 'LEARN_ACTIVITY',
  RECORDING = 'RECORDING',
  PERFECT_QUIZ = 'PERFECT_QUIZ',
  PERFECT_MIX_AND_MATCH = 'PERFECT_MIX_AND_MATCH',
  COMPLETE_FLASHCARDS = 'COMPLETE_FLASHCARDS',
  CHAT = 'CHAT',
  CALENDAR_CONNECTED = 'CALENDAR_CONNECTED'
}

export type QuizQuestionId = Branded<`${string}:${number}`, 'QuizQuestionId'>

export type QuizSessionId = Branded<string, 'QuizSessionId'>
export const makeQuizSessionId = make<QuizSessionId>()

export type LearnBatchId = Branded<string, 'LearnBatchId'>
export const makeLearnBatchId = make<LearnBatchId>()

export type SummaryJobId = Branded<string, 'SummaryJobId'>
export const makeSummaryJobId = make<SummaryJobId>()

export type ActionItemsJobId = Branded<string, 'ActionItemsJobId'>
export const makeActionItemsJobId = make<ActionItemsJobId>()

export enum QuizQuestionStatus {
  UNANSWERED = 'UNANSWERED',
  CORRECT = 'CORRECT',
  INCORRECT = 'INCORRECT',
  SKIPPED = 'SKIPPED',
  FLAGGED = 'FLAGGED'
}

export type FinishedQuizQuestionStatus = Exclude<QuizQuestionStatus, QuizQuestionStatus.UNANSWERED>

export enum MultipleChoiceResponse {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D'
}

// Product-level flashcard rating sent by clients. Mapped to an FSRS Rating via toFSRSRating().
export enum FlashcardRating {
  SKIP = 'SKIP', // FSRS Again — full stability reset
  HARD = 'HARD', // FSRS Hard
  MEDIUM = 'MEDIUM', // FSRS Good
  EASY = 'EASY' // FSRS Easy
}

export enum QuizJobStatus {
  NEW = 'NEW',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export enum QuizJobType {
  QUIZ = 'QUIZ',
  FLASHCARD = 'FLASHCARD',
  TRUE_OR_FALSE = 'TRUE_OR_FALSE'
}

export enum VanityCodeType {
  Ambassador = 'ambassador',
  Event = 'event'
}

export enum SummaryJobStatus {
  NEW = 'NEW',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export enum StudyChatExchangeStatus {
  NEW = 'NEW',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

// AWAITING_UPLOAD covers the window between minting the signed upload URL and the client's PUT
// landing; the job only enters PENDING once there is something to parse.
export enum SyllabusParseJobStatus {
  AWAITING_UPLOAD = 'AWAITING_UPLOAD',
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export enum StudyMaterialSource {
  NOTE_HANDWRITING = 'note_handwriting',
  NOTE_PDF = 'note_pdf',
  NOTE_TEXT = 'note_text',
  LIVE_TRANSCRIPT = 'live_transcript'
}

export enum LiveTranscriptionSessionStatus {
  ACTIVE = 'ACTIVE',
  FINALIZING = 'FINALIZING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export interface LiveTranscriptionSessionResult {
  id: string
  subscriberId?: string
  status: LiveTranscriptionSessionStatus
  transcriptionText: string
  smartNotesText: string
  lastAudioChunkIndexConsumed: number
}

export enum WhisperImplementation {
  DISTIL = 'DISTIL',
  FASTER = 'FASTER',
  FASTER_BATCHED = 'FASTER_BATCHED'
}

export enum ErrorReason {
  'SPEECH_NOT_DETECTED' = 'SPEECH_NOT_DETECTED',
  'ATTEMPTS_EXHAUSTED' = 'ATTEMPTS_EXHAUSTED'
}

export enum LearnErrorReason {
  TRANSCRIPT_DOES_NOT_EXIST = 'TRANSCRIPT_DOES_NOT_EXIST',
  INPUT_TOO_SHORT = 'INPUT_TOO_SHORT',
  LLM_INVALID_JSON = 'LLM_INVALID_JSON',
  LLM_IMPROPER_SCHEMA = 'LLM_IMPROPER_SCHEMA',
  PROCESSING_FAILURE = 'PROCESSING_FAILURE',
  RESULT_RETRIEVAL_FAILURE = 'RESULT_RETRIEVAL_FAILURE',
  BUCKET_RESULT_NOT_FOUND = 'BUCKET_RESULT_NOT_FOUND',
  INSUFFICIENT_CONTEXT = 'INSUFFICIENT_CONTEXT',
  LLM_UNEXPECTED_RESPONSE = 'LLM_UNEXPECTED_RESPONSE',
  // Terminal: the job was retired because its batch was superseded, or it stalled with no worker to
  // finish it. Never retried (excluded from shouldRetryErrorReason) so the note-wide allQuizJobs()
  // join can't resurrect it into a newer batch.
  JOB_OBSOLETE = 'JOB_OBSOLETE'
}

export enum OrganizationType {
  EDU = 'EDU',
  B2B = 'B2B'
}

export enum StripeSubscriptionType {
  B2B = 'B2B', // For device-based organizations (Device Licenses in Stripe)
  DeviceOrg = 'DeviceOrg', // TODO: (jy) Migrate to use this instead of the B2B value above
  Business = 'Business', // For account-based businesses
  Consumer = 'Consumer'
}

export interface IASRJobResultSegment {
  startTime: number
  endTime: number
  text: string
  confidence: number
  words?: IASRJobResultSegmentWord[]
  /** An `ISpeaker.id`. Absent when nothing was attributed to this segment. */
  speaker?: string
}

export interface IASRJobResultSegmentWord {
  start: number
  end: number
  word: string
  probability: number
}

/** One voice in one recording; the same person in two recordings gets two ids. */
export interface ISpeaker {
  /** 1-based by first appearance in the recording, as a string: "1", "2", … */
  id: string
}

export interface IASRJobResults {
  text: string
  segments: IASRJobResultSegment[]
  /** Ordered by first appearance. Absent when nothing was attributed. */
  speakers?: ISpeaker[]
}

export interface IASRJobPayload {
  sha512Hash: string
  createdAt: number

  extension: string

  audioCreationDate?: number
  isSubscriber: boolean
  subscriberId?: string

  audioDurationMs?: number
  queueInsertionTime: number
  language?: LanguageCode
}

export type LlmJobType = 'StudyMaterialsBatchJob' | 'StudyChatExchangeJob'

export interface LlmJobPayload {
  type: LlmJobType
  queueInsertionTime?: number
}

export interface StudyMaterialsBatchJobPayload extends LlmJobPayload {
  studyMaterialsBatchId: string
}

export interface Timestamp {
  start: number
  end: number
}

export interface NoteFamilyTree {
  noteName: string
  noteId: string
  author: string
  cardinality: number
  children: NoteFamilyTree[]
}

export type UserLinkMap = {
  [screenname: string]: {
    userId: string
    avatarUpdatedString?: string
  }
}

export interface EnvMetadata {
  deployed: boolean
  env: string
  baseUrl: string
  pull?: {
    author: string
    authorAvatar?: string
    title: string
    url: string
    id: number
    updated_at: number
    body: string
  }
}

export interface OctokitMilestone {
  title: string
  description: string
  html_url: string
  number: string
  open_issues: number
  closed_issues: number
  due_on: string | null
}

export interface OctokitLabel {
  name: string
}

export interface OctokitIssue {
  title: string
  number: number
  state: 'open' | 'closed'
  html_url: string
  labels: OctokitLabel[]
  assignees: OctokitUser[]
}

export interface OctokitUser {
  login: string
  avatar_url: string
  html_url: string
}

// https://devblogs.microsoft.com/typescript/announcing-typescript-4-1/#recursive-conditional-types
export type Awaited<T> = T extends PromiseLike<infer U> ? Awaited<U> : T

/**
 * A type which requires only the keys from **T** you specify, all others are optional
 * **For example**:
 * ``` ts
 * type A = {a: string, b: string, c: string}
 * type B = AtLeast<A, "b">
 * ```
 * `B` **becomes**:
 * ``` ts
 * {a: string | undefined, b: string, c: string | undefined}
 * ```
 */
export type AtLeast<T, K extends keyof T> = Partial<T> & Pick<T, K>

interface LoggableBase {
  message: string
  location?: string
}

export type Loggable = LoggableBase & {
  [key: string]: any
}

export interface RenderedCanvasMediaObjectProps {
  class: string
  x: number
  y: number
  width: number
  height: number
  rotationDegrees: number
  paperIdentifier: string
  textWrapMode: string
  cornerMode: string
  attributedText?: any[]
}

export interface RenderedMathMediaObjectProps extends Omit<RenderedCanvasMediaObjectProps, 'paperIdentifier'> {
  latex: string
  color: string
}

export interface RenderedImageObjectProps extends RenderedCanvasMediaObjectProps {
  isGif: boolean
  cropRect: any
  filename: string
  isCaptionEnabled: boolean
  captionFieldText?: string
  captionFontSize?: number
  captionFontColor?: string
  captionIsUnderlined?: boolean
  captionFontName?: string
}

export enum EducationLevel {
  K_12 = 'K_12',
  UNIVERSITY = 'UNIVERSITY',
  POST_GRADUATE = 'POST_GRADUATE',
  VOCATIONAL = 'VOCATIONAL',
  CONTINUING_EDUCATION = 'CONTINUING_EDUCATION',
  OTHER = 'OTHER'
}

// A null preference means "standard", so there is no STANDARD value to store.
export enum AiQuizDifficulty {
  EASIER = 'EASIER',
  HARDER = 'HARDER'
}

export enum AiSummaryDetail {
  BRIEF = 'BRIEF',
  DETAILED = 'DETAILED'
}

export enum GraduationTerm {
  SPRING = 'SPRING',
  SUMMER = 'SUMMER',
  FALL = 'FALL',
  WINTER = 'WINTER'
}

export enum LicenseEnforcement {
  NONE = 'NONE',
  WARNING = 'WARNING',
  STRICT = 'STRICT'
}

export enum OAuthProvider {
  APPLE = 'Apple',
  GOOGLE = 'Google',
  MICROSOFT = 'Microsoft'
}
export const oauthProviders = ['Apple', 'Google', 'Microsoft'] as const
export const oauthProvidersSet = new Set<string>(oauthProviders)

export const oauthPlatforms = ['WEB', 'IOS', 'ANDROID', 'MISSING'] as const
export const oauthPlatformsSet = new Set<string>(oauthPlatforms)

export enum BusinessUserRole {
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
  OWNER = 'OWNER'
}

/**
 * Whether a business-owner-change finding stops the swap outright or is context the admin
 * should read before deciding.
 */
export enum BusinessOwnerChangeFindingSeverity {
  BLOCKING = 'BLOCKING',
  WARNING = 'WARNING'
}

/**
 * Machine-readable identity for each thing a business-owner-change preview can turn up, so the
 * admin tool can branch on the situation without matching against message text.
 */
export enum BusinessOwnerChangeFindingCode {
  EXISTING_OWNER_NOT_FOUND = 'EXISTING_OWNER_NOT_FOUND',
  NEW_OWNER_NOT_FOUND = 'NEW_OWNER_NOT_FOUND',
  OWNERS_ARE_THE_SAME_USER = 'OWNERS_ARE_THE_SAME_USER',
  EXISTING_OWNER_OWNS_NO_BUSINESS = 'EXISTING_OWNER_OWNS_NO_BUSINESS',
  EXISTING_OWNER_OWNS_MULTIPLE_BUSINESSES = 'EXISTING_OWNER_OWNS_MULTIPLE_BUSINESSES',
  NEW_OWNER_NOT_IN_BUSINESS = 'NEW_OWNER_NOT_IN_BUSINESS',
  BUSINESS_HAS_NO_SUBSCRIPTION = 'BUSINESS_HAS_NO_SUBSCRIPTION',
  EXISTING_OWNER_HAS_NO_CUSTOMER_ID = 'EXISTING_OWNER_HAS_NO_CUSTOMER_ID',
  EXISTING_OWNER_HAS_ACTIVE_CONSUMER_SUBSCRIPTION = 'EXISTING_OWNER_HAS_ACTIVE_CONSUMER_SUBSCRIPTION',
  NEW_OWNER_HAS_DIFFERENT_CUSTOMER_ID = 'NEW_OWNER_HAS_DIFFERENT_CUSTOMER_ID',
  NEW_OWNER_HAS_ACTIVE_CONSUMER_SUBSCRIPTION = 'NEW_OWNER_HAS_ACTIVE_CONSUMER_SUBSCRIPTION'
}

/** Finding codes that make a keep-the-subscription swap impossible, though a transfer may still work. */
export const BUSINESS_OWNER_KEEP_SUBSCRIPTION_BLOCKERS: readonly BusinessOwnerChangeFindingCode[] = [
  BusinessOwnerChangeFindingCode.EXISTING_OWNER_HAS_NO_CUSTOMER_ID,
  BusinessOwnerChangeFindingCode.EXISTING_OWNER_HAS_ACTIVE_CONSUMER_SUBSCRIPTION,
  BusinessOwnerChangeFindingCode.NEW_OWNER_HAS_DIFFERENT_CUSTOMER_ID
]

export enum SSORequirement {
  NONE = 'none',
  GOOGLE = 'google',
  MICROSOFT = 'microsoft'
}

export enum OrganizationRole {
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
  OWNER = 'OWNER'
}

export enum OrganizationStatus {
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED'
}

export enum Platform {
  WEB = 'WEB',
  IOS = 'IOS',
  ANDROID = 'ANDROID',
  MISSING = 'MISSING'
}
export enum StripeSubscriptionStatus {
  ACTIVE = 'ACTIVE',
  INCOMPLETE = 'INCOMPLETE',
  INCOMPLETE_EXPIRED = 'INCOMPLETE_EXPIRED',
  TRIALING = 'TRIALING',
  PAST_DUE = 'PAST_DUE',
  CANCELED = 'CANCELED',
  UNPAID = 'UNPAID',
  PAUSED = 'PAUSED'
}

export const ACTIVE_STRIPE_STATUSES: StripeSubscriptionStatus[] = [
  StripeSubscriptionStatus.ACTIVE,
  StripeSubscriptionStatus.TRIALING,
  StripeSubscriptionStatus.PAST_DUE
]

export enum AuthCheckerRoles {
  SUPER_USER = 'SUPER_USER',
  ADMIN = 'ADMIN',
  EMPLOYEE = 'EMPLOYEE',

  BUSINESS_ADMIN = 'BUSINESS_ADMIN',
  // Admin/owner actions that stay available while the business subscription is restricted,
  // e.g. removing users so a restricted business can downgrade its seat count
  BUSINESS_ADMIN_EVEN_WHILE_RESTRICTED = 'BUSINESS_ADMIN_EVEN_WHILE_RESTRICTED',
  BUSINESS_MEMBER = 'BUSINESS_MEMBER',
  BUSINESS_OWNER = 'BUSINESS_OWNER',
  BUSINESS_OWNER_EVEN_WHILE_RESTRICTED = 'BUSINESS_OWNER_EVEN_WHILE_RESTRICTED',
  BUSINESS_VIEWER = 'BUSINESS_VIEWER', // Any read-only business action

  ORG_OWNER = 'ORG_OWNER',
  ORG_ADMIN = 'ORG_ADMIN',
  ORG_MEMBER = 'ORG_MEMBER'
}

export enum WorkspacePermissionLevels {
  VIEWER = 'VIEWER',
  COMMENTER = 'COMMENTER',
  EDITOR = 'EDITOR',
  OWNER = 'OWNER'
}

export enum LifetimeGrantStatus {
  ACTIVE = 'ACTIVE',
  REFUNDED = 'REFUNDED'
}

/**
 * String of the format "site-timestamp"
 */
export type IdString = Branded<`${number}-${number}`, 'IdString'>
export const makeIdString = make<IdString>()

export type SeqIdString = Branded<`${number}-${number}-${number}`, 'SeqIdString'>
export const makeSeqIdString = make<SeqIdString>()

export enum AsrErrorCode {
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  NOT_AUTHORIZED = 'NOT_AUTHORIZED',
  NOT_SUBSCRIBED = 'NOT_SUBSCRIBED',
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
  JOB_NOT_FOUND = 'JOB_NOT_FOUND',
  JOB_ALREADY_PROCESSED = 'JOB_ALREADY_PROCESSED',
  JOB_PROCESSING = 'JOB_PROCESSING'
}

export enum DiarizationErrorCode {
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  NOT_AUTHORIZED = 'NOT_AUTHORIZED',
  NOT_SUBSCRIBED = 'NOT_SUBSCRIBED',
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
  JOB_NOT_FOUND = 'JOB_NOT_FOUND',
  JOB_ALREADY_PROCESSED = 'JOB_ALREADY_PROCESSED',
  JOB_PROCESSING = 'JOB_PROCESSING'
}

/**
 * Cloud Storage types
 */

export type AudioExtension = keyof typeof audioFileExtensionToContentType
export type AudioFileContentType = (typeof audioFileExtensionToContentType)[AudioExtension]

export type NonAudioExtension = keyof typeof nonAudioFileExtensionToContentType
export type NonAudioFileContentType = (typeof nonAudioFileExtensionToContentType)[NonAudioExtension]

export type FileExtension = AudioExtension | Exclude<NonAudioExtension, 'image'>
export type FileType = AudioFileContentType | NonAudioFileContentType
export type TimeoutHandle = ReturnType<typeof setTimeout>

/**
 * The built-in Omit collapses unions, breaking discriminants if present:
 * ```
 * type A = { kind: 'a'; x: number; shared: string }
 * type B = { kind: 'b'; y: number; shared: string }
 * type Union = A | B
 *
 * type Collapsed = Omit<Union, 'shared'> // = { kind: 'a' | 'b'; x?: number; y?: number }
 * type Distributed = DistributiveOmit<Union, 'shared'> // = Omit<A, 'shared'> | Omit<B, 'shared'>
 * ```
 *
 * Library types aren't distributive due to performance implications:
 * https://github.com/microsoft/TypeScript/issues/31501#issuecomment-494520633
 *
 * This is the recommended fix:
 * https://github.com/microsoft/TypeScript/wiki/FAQ#add-a-key-constraint-to-omit
 */
export type DistributiveOmit<T, K extends keyof T> = {
  [P in keyof T as Exclude<P, K & PropertyKey>]: T[P]
}
